import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { useInView } from '@/hooks/use-in-view'
import {
  Workflow,
  Users,
  TrendingUp,
  Key,
  UserCheck,
  Server,
} from 'lucide-react'

interface FeatureCardProps {
  icon: React.ReactNode
  titleKey: string
  descriptionKey: string
  delay: number
}

function FeatureCard({ icon, titleKey, descriptionKey, delay }: FeatureCardProps) {
  const { t } = useTranslation('landing')
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.2 })

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <Card variant="glass" className="p-6 h-full hover:border-indigo-500/50 transition-all group">
        <div className="flex flex-col items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform">
            {icon}
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-white">{t(titleKey)}</h3>
            <p className="text-gray-400 leading-relaxed">{t(descriptionKey)}</p>
          </div>
        </div>
      </Card>
    </div>
  )
}

const features = [
  { icon: <Workflow className="w-6 h-6" />, titleKey: 'features.visual.title', descriptionKey: 'features.visual.description' },
  { icon: <Users className="w-6 h-6" />, titleKey: 'features.teams.title', descriptionKey: 'features.teams.description' },
  { icon: <TrendingUp className="w-6 h-6" />, titleKey: 'features.roi.title', descriptionKey: 'features.roi.description' },
  { icon: <Key className="w-6 h-6" />, titleKey: 'features.byok.title', descriptionKey: 'features.byok.description' },
  { icon: <UserCheck className="w-6 h-6" />, titleKey: 'features.hitl.title', descriptionKey: 'features.hitl.description' },
  { icon: <Server className="w-6 h-6" />, titleKey: 'features.enterprise.title', descriptionKey: 'features.enterprise.description' },
]

export function FeaturesSection() {
  const { t } = useTranslation('landing')
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.1 })

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/5 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-16 space-y-4 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white">{t('features.title')}</h2>
          <p className="text-lg text-gray-400">{t('features.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.titleKey}
              icon={feature.icon}
              titleKey={feature.titleKey}
              descriptionKey={feature.descriptionKey}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
